import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Minus, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { errMsg, fmt, fileUrl, PAYMENTS, SOURCES } from "@/lib/api";
import { usePanel } from "./PanelContext";

const ORDER_SOURCES = ["telefon", "gel_al", "masa", "yemeksepeti", "trendyol", "migros"];

export function PaymentPicker({ value, onChange, prefix = "payment" }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {Object.entries(PAYMENTS).map(([k, l]) => (
        <button type="button" key={k} onClick={() => onChange(k)} data-testid={`${prefix}-${k}`}
          className={`rounded-xl border px-2 py-2.5 text-sm font-semibold transition-colors ${value === k ? "bg-[#0F1F45] text-white border-[#0F1F45]" : "bg-white hover:border-[#FF6B0A]"}`}>{l}</button>
      ))}
    </div>
  );
}

const EMPTY = { source: "telefon", customer_name: "", customer_phone: "", address: "", note: "", table_id: "", payment_method: "nakit" };

export default function NewOrderDialog({ open, onOpenChange, prefill }) {
  const { api, refreshOrders } = usePanel();
  const [form, setForm] = useState(EMPTY);
  const [cart, setCart] = useState([]);
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [tables, setTables] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  const [custom, setCustom] = useState({ name: "", price: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY, ...(prefill || {}) });
    setCart([]);
    Promise.all([api.get("/products"), api.get("/categories"), api.get("/tables")]).then(([p, c, t]) => {
      setProducts(p.data.filter((x) => x.available)); setCats(c.data); setTables(t.data);
    }).catch((e) => toast.error(errMsg(e)));
  }, [open, prefill, api]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const add = (p) => setCart((c) => {
    const key = p.id || p.name;
    const ex = c.find((i) => i.key === key);
    return ex ? c.map((i) => (i.key === key ? { ...i, qty: i.qty + 1 } : i)) : [...c, { key, product_id: p.id || null, name: p.name, price: p.price, qty: 1 }];
  });
  const changeQty = (key, d) => setCart((c) => c.map((i) => (i.key === key ? { ...i, qty: i.qty + d } : i)).filter((i) => i.qty > 0));
  const total = useMemo(() => cart.reduce((s, i) => s + i.price * i.qty, 0), [cart]);
  const shown = catFilter === "all" ? products : products.filter((p) => p.category_id === catFilter);

  const lookup = async () => {
    if ((form.customer_phone || "").length < 7) return;
    try {
      const { data } = await api.get("/customers/lookup", { params: { phone: form.customer_phone } });
      if (data.order_count) {
        setForm((f) => ({ ...f, customer_name: f.customer_name || data.name, address: f.address || data.address }));
        toast.info(`Kayıtlı müşteri: ${data.name} (${data.order_count} sipariş)`);
      }
    } catch { /* ignore */ }
  };

  const submit = async () => {
    if (!cart.length) return toast.error("Sepete ürün ekleyin");
    if (form.source === "masa" && !form.table_id) return toast.error("Masa seçin");
    setSaving(true);
    try {
      const body = { ...form, table_id: form.source === "masa" ? form.table_id : null,
        items: cart.map((i) => (i.product_id ? { product_id: i.product_id, qty: i.qty } : { name: i.name, price: i.price, qty: i.qty })) };
      const { data } = await api.post("/orders", body);
      toast.success(`Sipariş #${data.no} oluşturuldu`);
      onOpenChange(false);
      refreshOrders();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="new-order-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Yeni Sipariş</DialogTitle></DialogHeader>
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
          <div>
            <div className="flex gap-2 overflow-x-auto pb-2">
              <Button size="sm" variant={catFilter === "all" ? "default" : "outline"} className="rounded-full" onClick={() => setCatFilter("all")} data-testid="new-order-cat-all">Tümü</Button>
              {cats.map((c) => <Button key={c.id} size="sm" variant={catFilter === c.id ? "default" : "outline"} className="rounded-full whitespace-nowrap" onClick={() => setCatFilter(c.id)} data-testid={`new-order-cat-${c.id}`}>{c.name}</Button>)}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2 max-h-[340px] overflow-y-auto pr-1">
              {shown.map((p) => (
                <button key={p.id} onClick={() => add(p)} className="text-left rounded-2xl border bg-white overflow-hidden hover:border-[#FF6B0A] transition-colors" data-testid={`new-order-product-${p.id}`}>
                  {p.image ? <img src={fileUrl(p.image)} alt={p.name} className="h-20 w-full object-cover" /> : <div className="h-20 bg-slate-100" />}
                  <div className="p-2.5"><p className="text-sm font-semibold line-clamp-1">{p.name}</p><p className="text-xs text-[#FF6B0A] font-bold">{fmt(p.price)}</p></div>
                </button>
              ))}
              {!shown.length && <p className="col-span-full text-sm text-slate-500 py-6">Menüde ürün yok. Menü sayfasından ürün ekleyin veya aşağıdan serbest ürün girin.</p>}
            </div>
            <div className="flex gap-2 mt-3">
              <Input placeholder="Serbest ürün adı" value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })} data-testid="new-order-custom-name" />
              <Input placeholder="Fiyat" type="number" className="w-28" value={custom.price} onChange={(e) => setCustom({ ...custom, price: e.target.value })} data-testid="new-order-custom-price" />
              <Button variant="outline" data-testid="new-order-custom-add" onClick={() => { if (custom.name && custom.price) { add({ name: custom.name, price: parseFloat(custom.price) }); setCustom({ name: "", price: "" }); } }}>Ekle</Button>
            </div>
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Sipariş yöntemi</Label>
                <Select value={form.source} onValueChange={(v) => set("source", v)}>
                  <SelectTrigger data-testid="new-order-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ORDER_SOURCES.map((s) => <SelectItem key={s} value={s} data-testid={`new-order-source-${s}`}>{SOURCES[s]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.source === "masa" && (
                <div>
                  <Label>Masa</Label>
                  <Select value={form.table_id} onValueChange={(v) => set("table_id", v)}>
                    <SelectTrigger data-testid="new-order-table-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                    <SelectContent>{tables.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {form.source !== "masa" && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Telefon" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} onBlur={lookup} data-testid="new-order-phone-input" />
                  <Input placeholder="Müşteri adı" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="new-order-name-input" />
                </div>
                <Textarea placeholder="Adres (mahalle, sokak, no, ilçe, şehir)" rows={2} value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="new-order-address-input" />
              </>
            )}
            <div className="rounded-2xl border bg-slate-50 p-3 space-y-2 max-h-48 overflow-y-auto" data-testid="new-order-cart">
              {cart.map((i) => (
                <div key={i.key} className="flex items-center gap-2 text-sm">
                  <span className="flex-1 truncate">{i.name}</span>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => changeQty(i.key, -1)} data-testid={`cart-minus-${i.key}`}>{i.qty === 1 ? <Trash2 className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}</Button>
                  <span className="w-5 text-center font-bold">{i.qty}</span>
                  <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => changeQty(i.key, 1)} data-testid={`cart-plus-${i.key}`}><Plus className="h-3.5 w-3.5" /></Button>
                  <span className="w-20 text-right font-mono-num">{fmt(i.price * i.qty)}</span>
                </div>
              ))}
              {!cart.length && <p className="text-sm text-slate-500">Sepet boş</p>}
            </div>
            <Label>Ödeme yöntemi</Label>
            <PaymentPicker value={form.payment_method} onChange={(v) => set("payment_method", v)} prefix="new-order-payment" />
            <Input placeholder="Sipariş notu" value={form.note} onChange={(e) => set("note", e.target.value)} data-testid="new-order-note-input" />
            <div className="flex items-center justify-between pt-2">
              <span className="font-display text-2xl font-extrabold" data-testid="new-order-total">{fmt(total)}</span>
              <Button className="rounded-full bg-[#FF6B0A] hover:bg-[#E55A00] px-8" disabled={saving} onClick={submit} data-testid="new-order-submit-btn">Siparişi Oluştur</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
